import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Mic, Square, Loader2, Save, Download, Trash2, Pencil, X,
  LogOut, CalendarDays, PoundSterling, Grid,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/cd5s20vv_isalon2022-01.png";

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const humanDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return iso;
  return `${ordinal(d.getDate())} ${d.toLocaleString('en-GB', { month: 'long' })} ${d.getFullYear()}`;
};

const todayIso = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD local
const money = (v) => `£${(parseFloat(v) || 0).toFixed(2)}`;

const emptyForm = () => ({
  dateIso: todayIso(),
  emma_card: '',
  emma_cash: '',
  se_card: '',
  se_cash: '',
});

const AmountField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-white/60 text-xs font-sans mb-1 block">{label}</label>
    <div className="relative">
      <PoundSterling size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-isalonuk-accent" />
      <Input
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0.00"
        className="bg-black border-white/20 text-white pl-9 text-lg"
        data-testid={testid}
      />
    </div>
  </div>
);

const TakingsPage = () => {
  const { user, logout, authFetch } = useAuth();
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [takings, setTakings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const emmaTotal = (parseFloat(form.emma_card) || 0) + (parseFloat(form.emma_cash) || 0);
  const seTotal = (parseFloat(form.se_card) || 0) + (parseFloat(form.se_cash) || 0);

  const loadTakings = useCallback(async () => {
    try {
      const res = await authFetch('/api/takings');
      const data = await res.json();
      setTakings(data.takings || []);
    } catch (err) {
      if (err.message) toast.error(err.message);
    }
  }, [authFetch]);

  useEffect(() => { loadTakings(); }, [loadTakings]);

  const resetForm = () => {
    setForm(emptyForm());
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.dateIso) { toast.error('Please choose a date'); return; }
    setSaving(true);
    const payload = {
      date: humanDate(form.dateIso),
      date_iso: form.dateIso,
      emma_card: parseFloat(form.emma_card) || 0,
      emma_cash: parseFloat(form.emma_cash) || 0,
      se_card: parseFloat(form.se_card) || 0,
      se_cash: parseFloat(form.se_cash) || 0,
    };
    try {
      const res = await authFetch(editingId ? `/api/takings/${editingId}` : '/api/takings', {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Could not save entry');
      toast.success(editingId ? 'Entry updated' : 'Takings saved');
      resetForm();
      loadTakings();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setForm({
      dateIso: t.date_iso || todayIso(),
      emma_card: t.emma_card ? String(t.emma_card) : '',
      emma_cash: t.emma_cash ? String(t.emma_cash) : '',
      se_card: t.se_card ? String(t.se_card) : '',
      se_cash: t.se_cash ? String(t.se_cash) : '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this takings entry? This cannot be undone.')) return;
    try {
      const res = await authFetch(`/api/takings/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Could not delete entry');
      toast.success('Entry deleted');
      if (editingId === id) resetForm();
      loadTakings();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  const handleExport = async () => {
    try {
      const res = await authFetch('/api/takings/export');
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `isalon-takings-${todayIso()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (err) {
      toast.error(err.message || 'Export failed');
    }
  };

  // ---- Voice recording ----
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await sendVoice(blob);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch (err) {
      toast.error('Microphone access denied. Please allow microphone access.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      setProcessing(true);
    }
  };

  const sendVoice = async (blob) => {
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'recording.webm');
      const res = await authFetch('/api/takings/voice', { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Transcription failed');
      if (data.parsed) {
        setForm({
          dateIso: data.parsed.date_iso || todayIso(),
          emma_card: data.parsed.emma_card ? String(data.parsed.emma_card) : '',
          emma_cash: data.parsed.emma_cash ? String(data.parsed.emma_cash) : '',
          se_card: data.parsed.se_card ? String(data.parsed.se_card) : '',
          se_cash: data.parsed.se_cash ? String(data.parsed.se_cash) : '',
        });
        toast.success('Fields filled — please review before saving');
      } else {
        toast.error(data.error || 'Could not read the figures. Please enter manually.');
      }
    } catch (err) {
      toast.error(err.message || 'Voice entry failed');
    } finally {
      setProcessing(false);
    }
  };

  const grandFromRow = (t) => (t.emma_total + t.se_total);

  return (
    <div className="min-h-screen bg-black py-8 px-4" data-testid="takings-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link to="/"><img src={LOGO_URL} alt="iSalon UK" className="h-10" /></Link>
            <div>
              <h1 className="font-heading text-2xl text-white">Daily Takings</h1>
              <p className="text-white/50 text-sm">Signed in as {user?.name || user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/backoffice">
              <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10" data-testid="nav-calendars">
                <Grid size={16} className="mr-2" /> Calendars
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={logout} className="border-white/20 text-white hover:bg-white/10" data-testid="logout-btn">
              <LogOut size={16} className="mr-2" /> Log Out
            </Button>
          </div>
        </div>

        {/* Entry Card */}
        <Card className="bg-zinc-900 border-white/10 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-lg text-white">
                {editingId ? 'Edit Entry' : 'New Entry'}
              </h2>
              {/* Mic */}
              <div className="flex items-center gap-3">
                {processing ? (
                  <div className="flex items-center gap-2 text-isalonuk-accent text-sm" data-testid="voice-processing">
                    <Loader2 size={18} className="animate-spin" /> Listening…
                  </div>
                ) : recording ? (
                  <Button
                    onClick={stopRecording}
                    className="bg-red-500 hover:bg-red-600 text-white rounded-full animate-pulse"
                    data-testid="voice-stop"
                  >
                    <Square size={16} className="mr-2" /> Stop
                  </Button>
                ) : (
                  <Button
                    onClick={startRecording}
                    className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black rounded-full"
                    data-testid="voice-record"
                  >
                    <Mic size={16} className="mr-2" /> Speak Takings
                  </Button>
                )}
              </div>
            </div>

            <p className="text-white/40 text-xs mb-5">
              Tip: tap “Speak Takings” and say e.g. <span className="text-white/60">“24th May 2025, Emma card 215 cash 100, self-employed cash zero card 200.”</span>
            </p>

            {/* Date */}
            <div className="mb-5">
              <label className="text-white/60 text-xs font-sans mb-1 block">Date</label>
              <div className="relative max-w-xs">
                <CalendarDays size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-isalonuk-accent" />
                <Input
                  type="date"
                  value={form.dateIso}
                  onChange={(e) => setField('dateIso', e.target.value)}
                  className="bg-black border-white/20 text-white pl-9 [color-scheme:dark]"
                  data-testid="taking-date"
                />
              </div>
              {form.dateIso && <p className="text-white/40 text-xs mt-1">{humanDate(form.dateIso)}</p>}
            </div>

            {/* Emma */}
            <div className="mb-4">
              <p className="text-isalonuk-accent font-sans text-sm font-semibold mb-2">Emma’s Takings</p>
              <div className="grid grid-cols-2 gap-4">
                <AmountField label="Card" value={form.emma_card} onChange={(v) => setField('emma_card', v)} testid="emma-card" />
                <AmountField label="Cash" value={form.emma_cash} onChange={(v) => setField('emma_cash', v)} testid="emma-cash" />
              </div>
              <p className="text-white/70 text-sm mt-2" data-testid="emma-total">
                Emma total: <span className="text-white font-semibold">{money(emmaTotal)}</span>
              </p>
            </div>

            {/* Self-employed */}
            <div className="mb-6">
              <p className="text-isalonuk-accent font-sans text-sm font-semibold mb-2">Self-Employed Takings</p>
              <div className="grid grid-cols-2 gap-4">
                <AmountField label="Card" value={form.se_card} onChange={(v) => setField('se_card', v)} testid="se-card" />
                <AmountField label="Cash" value={form.se_cash} onChange={(v) => setField('se_cash', v)} testid="se-cash" />
              </div>
              <p className="text-white/70 text-sm mt-2" data-testid="se-total">
                Self-employed total: <span className="text-white font-semibold">{money(seTotal)}</span>
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-semibold rounded-full flex-1 sm:flex-none sm:px-8"
                data-testid="save-taking"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <><Save size={16} className="mr-2" /> {editingId ? 'Update' : 'Save'}</>}
              </Button>
              {editingId && (
                <Button onClick={resetForm} variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full" data-testid="cancel-edit">
                  <X size={16} className="mr-2" /> Cancel
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* History */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg text-white">History</h2>
          <Button
            onClick={handleExport}
            variant="outline"
            size="sm"
            disabled={takings.length === 0}
            className="border-isalonuk-accent/50 text-isalonuk-accent hover:bg-isalonuk-accent/10 rounded-full"
            data-testid="export-csv"
          >
            <Download size={16} className="mr-2" /> Export CSV
          </Button>
        </div>

        {takings.length === 0 ? (
          <Card className="bg-zinc-900 border-white/10">
            <CardContent className="p-8 text-center text-white/40 text-sm">
              No takings recorded yet. Add your first entry above.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="takings-list">
            {takings.map((t) => (
              <Card key={t.id} className="bg-zinc-900 border-white/10" data-testid={`taking-row-${t.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-white font-semibold">{t.date}</p>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-xs">
                        <span className="text-white/60">Emma: card {money(t.emma_card)} · cash {money(t.emma_cash)} · <span className="text-isalonuk-accent font-semibold">{money(t.emma_total)}</span></span>
                        <span className="text-white/60">Self-emp: card {money(t.se_card)} · cash {money(t.se_cash)} · <span className="text-isalonuk-accent font-semibold">{money(t.se_total)}</span></span>
                      </div>
                      <p className="text-white/40 text-xs mt-1">Grand total: <span className="text-white">{money(grandFromRow(t))}</span></p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="icon" variant="outline" onClick={() => handleEdit(t)} className="h-8 w-8 border-white/20 text-white hover:bg-white/10" data-testid={`edit-${t.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button size="icon" variant="outline" onClick={() => handleDelete(t.id)} className="h-8 w-8 border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid={`delete-${t.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TakingsPage;
